import {
  CALLBACK_PATH,
  CONTEXT_SELECTION_PATH,
  DISABLED_OAUTH2_ROUTES, HOME_PATH,
  NO_CONTEXT_PATH,
  PROVIDER,
  SAFEA_V3_ENABLED,
  SAFEA_VERSION,
  SAFEA_VERSIONS
} from '~/constants/_core/safea-v3';
import {
  buildDefaultAuthorizeUrl,
  clearAuthorizeUrlState,
  getCallbackUrl,
  getClaimName,
  isIgnoredRedirectPath,
  isSamePath,
  mapUserInfoToUser
} from '~/helpers/_core/safea-v3';
import storage from '~/helpers/_core/storage';
import { OWNER } from '~/constants/_core/owner';

export default async ({ route, redirect, store, app }) => {
  if (!SAFEA_V3_ENABLED || process.server) {
    return;
  }

  // Same flag as SAML to disable the redirect for anonymous routes. At some point, we should rename this.
  const meta = route.meta || [];
  const { disableSaml } = meta.find(item => item.disableSaml) || {};

  if (disableSaml) {
    return;
  }

  if (DISABLED_OAUTH2_ROUTES.some(regex => regex.test(route.path))) {
    return;
  }

  const { $services, $token } = app;
  const { state } = store;
  const query = { ...route.query };

  async function fetchNewToken() {
    let result;
    try {
      result = await $services._core.oauthV3().token({
        client_id     : process.env.SAFEA_CLIENT_ID,
        code_verifier : storage.session.codeVerifier,
        redirect_uri  : getCallbackUrl(),
        code          : route.query.code
      });
    } catch (err) {
      console.error('[Cockpit] Authentication failure', err.response?.data);
      return { success: false, response: err.response };
    }

    $token.setTokens({
      idToken      : result.id_token,
      accessToken  : `${result.token_type} ${result.access_token}`,
      refreshToken : result.refresh_token
    });

    return { success: true, result };
  }

  /**
   * Will fetch the user info from Safe A v3 and populate the store with it.
   * Note that this function assumes that getUserInfo() will never fail, because:
   * - If the token is ok, the request will succeed
   * - If the access token is invalid, it assumes axios will intercept the request and refresh the token automatically
   * - If the access and refresh tokens are invalid, the user will be redirected to the authorize endpoint (through axios interceptor as well)
   *
   * @returns {Promise<void>}
   */
  async function fetchUserInfo() {
    const user = await $services._core.oauthV3().getUserInfo();

    store.commit('_core/user/SET_USER', mapUserInfoToUser(user, state._core.application.owner));
    store.commit('_core/user/SET_CURRENT_CONTEXT', user[getClaimName('context')] ?? []);
    store.commit('_core/user/SET_CONTEXTS', user[getClaimName('contexts')] ?? []);
    store.commit('_core/user/SET_PERMISSIONS', user[getClaimName('permissions')]);

    const impersonatedBy = user[getClaimName('impersonatedBy')];
    if (!!impersonatedBy) {
      store.commit('_core/auth/SET_IMPERSONATED_BY', impersonatedBy);
    }

    // If migrating, we still need to fetch the v2's user info (for roles)
    if (SAFEA_VERSION === SAFEA_VERSIONS.MIGRATING) {
      const userV2 = await $services._core.user().getUserInfo();
      const { data: { currentRole } } = userV2;
      if (currentRole?.tenantAlias) {
        store.commit('_core/tenant/SET_TENANT_ALIAS', currentRole.tenantAlias, { root: true });
      }
      if (currentRole?.iesAlias) {
        store.commit('_core/tenant/SET_IES_ALIAS', currentRole.iesAlias, { root: true });
      }
      await store.dispatch('_core/auth/processSignIn', userV2);
    }
  }

  async function authorize(params = {}) {
    const authorizeUrl = await buildDefaultAuthorizeUrl({ store, route }, params);
    return redirect(authorizeUrl);
  }

  function redirectToIdPErrorPage(description, requestId) {
    const idpUrl = PROVIDER.IDP_ERROR_PAGE;
    const params = new URLSearchParams({
      client_id         : process.env.SAFEA_CLIENT_ID,
      request_id        : requestId,
      error_description : description
    });
    if (!requestId) {
      params.delete('request_id');
    }

    return redirect(`${idpUrl}?${params.toString()}`);
  }

  async function handleTokenId(tokenId) {
    $token.clear();
    clearAuthorizeUrlState();

    const result = await $services._core.authV3().exchangeLegacyToken(tokenId);
    return authorize({
      v2RoleId        : result.v2RoleId ?? null,
      tenantUuid      : result.tenantUuid ?? null,
      institutionUuid : result.institutionUuid ?? null
    });
  }

  function handleLTI() {
    $token.clear();
    clearAuthorizeUrlState();
    return authorize();
  }

  function handleContextSelection() {
    const canAccessCurrentContext = store.getters['_core/user/canAccessCurrentContext'];
    const canAccessAtLeastOneContext = store.getters['_core/user/canAccessAtLeastOneContext'];

    const owner = store.state._core.application.owner;
    if (isSamePath(route.path, CONTEXT_SELECTION_PATH) && owner === OWNER.IES) {
      // The user ended up here by typing the URL, but there is no context to select in the institution hostname
      return HOME_PATH;
    }

    if (
      !canAccessAtLeastOneContext
      && !isSamePath(route.path, NO_CONTEXT_PATH)
    ) {
      return NO_CONTEXT_PATH;
    }

    if (isIgnoredRedirectPath(route.path)) {
      return null;
    }

    if (!canAccessCurrentContext && canAccessAtLeastOneContext) {
      return CONTEXT_SELECTION_PATH;
    }

    const doesNotHaveCurrentRole = !store.getters['_core/auth/hasCurrentRole'];
    if (doesNotHaveCurrentRole && SAFEA_VERSION === SAFEA_VERSIONS.MIGRATING) {
      return CONTEXT_SELECTION_PATH;
    }

    return null;
  }

  function parseUuid(uuid, propertyName) {
    if (typeof uuid === 'undefined' || uuid === null) {
      return uuid;
    }

    if (uuid.length !== 36) {
      throw new Error(`[Cockpit] OAuth2 failed - Received an invalid UUID for property "${propertyName}"`);
    }

    return uuid;
  }

  function parseId(id, propertyName) {
    if (typeof id === 'undefined' || id === null) {
      return id;
    }

    const value = parseInt(id, 10);
    if (Number.isNaN(value)) {
      throw new Error(`[Cockpit] OAuth2 failed - Received an invalid ID for property "${propertyName}"`);
    }

    return value;
  }

  function getSSOAuthorize() {
    const v2RoleId = parseId(query.v2RoleId, 'v2RoleId');
    const tenantUuid = parseUuid(query.tenantUuid, 'tenantUuid');
    const institutionUuid = parseUuid(query.institutionUuid, 'institutionUuid');

    // Cursed logic:
    // - undefined means it's not an SSO request (nobody sent the values)
    // - null means an access level change (empty values)
    if (typeof v2RoleId === 'undefined' && typeof tenantUuid === 'undefined' && typeof institutionUuid === 'undefined') {
      return { success: false, execute: null };
    }

    const currentTenantUuid = state._core.user.currentContext?.tenant?.uuid ?? state._core.tenant.uuid ?? null;
    const currentInstitutionUuid = state._core.user.currentContext?.institution?.uuid ?? store.state._core.tenant.institution.uuid ?? null;
    const currentRoleId = state._core.auth.currentRole?.role?.roleId ?? null;
    if (
      (v2RoleId !== currentRoleId)
      || (tenantUuid !== currentTenantUuid)
      || (institutionUuid !== currentInstitutionUuid)) {
      return {
        success : true,
        execute : () => authorize({
          v2RoleId,
          tenantUuid,
          institutionUuid
        })
      };
    }
    return {
      success : true,
      execute : () => {
        const queryCopy = { ...query };
        delete queryCopy.v2RoleId;
        delete queryCopy.tenantUuid;
        delete queryCopy.institutionUuid;

        return redirect({ path: route.path, query: queryCopy });
      }
    };
  }

  if (query.lti_launch) {
    return handleLTI();
  }

  const tokenId = query.tokenId;
  if (tokenId) {
    return handleTokenId(tokenId);
  }

  if (isSamePath(route.path, CALLBACK_PATH)) {
    const returnUrl = storage.session.returnUrl;
    if (!route.query.code || !route.query.state) {
      return redirectToIdPErrorPage(query.error_description ?? app.i18n.t('_core.oauth.errors.callbackError'));
    }

    // RFC: The binding value enables the client to verify the
    //    validity of the request by matching the binding value to the
    //    user-agent's authenticated state.
    if (route.query.state !== storage.session.state) {
      return redirectToIdPErrorPage(app.i18n.t('_core.oauth.errors.invalidState'));
    }

    const result = await fetchNewToken();
    if (!result.success) {
      return redirectToIdPErrorPage(result.response?.error_description ?? app.i18n.t('_core.oauth.errors.tokenExchangeFailed'));
    }

    await fetchUserInfo();

    const contextSelectionPath = handleContextSelection();
    if (contextSelectionPath) {
      return redirect(contextSelectionPath);
    }

    // Fetch the settings again, so they reflect the token's context (theme)
    await store.dispatch('_core/application/init');

    clearAuthorizeUrlState();
    // Redirect to the original URL
    // But this will trigger the middleware again, which should handle the returnUrl below :)
    // Nothing like sending the user in circles
    return redirect(returnUrl);
  }

  // If independent tabs is enabled, attempt to restore the last session from local storage before calling user info
  $token.restoreLastSession();

  // The user has no token set, just authorize
  if (!$token.getTokens()) {
    const ssoAuthorize = getSSOAuthorize();
    if (ssoAuthorize.success) {
      return ssoAuthorize.execute();
    }

    return authorize();
  }

  // User info hasn't been called yet
  // Call it to populate the store and redirect if it fails
  if (!state._core.user.id) {
    $token.updateStore();

    await fetchUserInfo();
    const ssoAuthorize = getSSOAuthorize();
    if (ssoAuthorize.success) {
      return ssoAuthorize.execute();
    }

    const returnUrl = handleContextSelection() || query.returnUrl;

    if (returnUrl) {
      return redirect(returnUrl);
    }
  } else {
    const contextSelectionPath = handleContextSelection();

    if (contextSelectionPath) {
      return redirect(contextSelectionPath);
    }
  }
};
